import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

// Cloudflare Turnstile — renderuje se samo ako je site key postavljen.
// Bez ključa forma radi kao i pre (backend tada takođe ne traži token).
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[čć]/g, 'c').replace(/š/g, 's').replace(/ž/g, 'z').replace(/đ/g, 'dj')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const Register: React.FC = () => {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    store_name: '',
    password: '',
    // Honeypot: skriven CSS-om, pravi korisnik ga nikad ne vidi ni popuni.
    // Backend odbija zahtev ako je popunjen.
    contact_url: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const [turnstileToken, setTurnstileToken] = useState('');

  const slug = useMemo(() => slugify(form.store_name), [form.store_name]);

  // Turnstile skript + widget. Preskače se u potpunosti bez site key-a.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current) return;
    const render = () => {
      const ts = (window as any).turnstile;
      if (!ts || !turnstileRef.current || turnstileRef.current.childElementCount) return;
      ts.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (t: string) => setTurnstileToken(t),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken(''),
      });
    };
    if ((window as any).turnstile) {
      render();
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true;
    s.onload = render;
    document.head.appendChild(s);
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug || slug.length < 2) {
      toast.error('Naziv prodavnice mora imati slova/brojeve');
      return;
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error('Molimo potvrdite da niste robot');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/auth/v1/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, turnstile_token: turnstileToken }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error_description || body.error || 'Greška');
      setDone(form.email);
    } catch (err: any) {
      toast.error(err.message);
      // Turnstile token je jednokratan — resetuj widget da korisnik može opet.
      if (TURNSTILE_SITE_KEY) {
        setTurnstileToken('');
        (window as any).turnstile?.reset();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resend = useCallback(async (email: string) => {
    setResending(true);
    try {
      await fetch('/auth/v1/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      // Endpoint uvek vraća ok (da ne otkriva koje adrese postoje).
      toast.success('Ako nalog postoji i nije verifikovan, poslali smo novi link.');
    } catch {
      toast.error('Slanje nije uspelo. Probajte kasnije.');
    } finally {
      setResending(false);
    }
  }, []);

  if (done) {
    return (
      <div style={page}>
        <div style={card}>
          <h1 style={{ marginTop: 0 }}>✉ Proverite email</h1>
          <p>
            Poslali smo verifikacioni link na <strong>{done}</strong>. Kliknite na
            dugme u mejlu da aktivirate svoju prodavnicu.
          </p>
          <p style={{ color: '#6b7280', fontSize: 13 }}>
            Link ističe za 24 sata. Ako ne vidite mejl proverite spam folder.
          </p>
          <button
            type="button"
            onClick={() => resend(done)}
            disabled={resending}
            style={{ ...primaryBtn, background: '#fff', color: '#0f172a', border: '1px solid #d1d5db' }}
          >
            {resending ? 'Šaljem...' : 'Pošalji link ponovo'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      <form onSubmit={submit} style={card}>
        <h1 style={{ marginTop: 0 }}>Registracija prodavnice</h1>

        <Field label="Ime">
          <input
            value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            style={input}
            required
          />
        </Field>
        <Field label="Prezime">
          <input
            value={form.last_name}
            onChange={(e) => setForm({ ...form, last_name: e.target.value })}
            style={input}
            required
          />
        </Field>
        <Field label="Email">
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            style={input}
            required
          />
        </Field>
        <Field label="Naziv prodavnice">
          <input
            value={form.store_name}
            onChange={(e) => setForm({ ...form, store_name: e.target.value })}
            style={input}
            placeholder="npr. Moja Prodavnica"
            required
          />
          <small style={{ color: '#6b7280', fontSize: 12 }}>
            Vaš subdomen:&nbsp;
            <strong style={{ color: '#0f172a' }}>
              {slug || 'xxx'}.serbia-shop.com
            </strong>
          </small>
        </Field>
        <Field label="Lozinka (min 8)">
          <input
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            style={input}
            minLength={8}
            required
          />
        </Field>

        {/* Honeypot — skriven od ljudi, vidljiv botovima koji popunjavaju sve
            inpute. Nije `type="hidden"` jer to botovi preskaču. */}
        <div aria-hidden="true" style={honeypot}>
          <label>
            Contact URL
            <input
              type="text"
              name="contact_url"
              tabIndex={-1}
              autoComplete="off"
              value={form.contact_url}
              onChange={(e) => setForm({ ...form, contact_url: e.target.value })}
            />
          </label>
        </div>

        {TURNSTILE_SITE_KEY && <div ref={turnstileRef} style={{ marginBottom: 14 }} />}

        <button type="submit" disabled={submitting} style={primaryBtn}>
          {submitting ? 'Registrujem...' : 'Registruj prodavnicu'}
        </button>
        <div style={{ marginTop: 16, fontSize: 13, color: '#6b7280' }}>
          Već imate nalog? <Link to="/login">Prijavite se</Link>
        </div>
      </form>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label style={{ display: 'block', marginBottom: 14 }}>
    <div style={{ fontSize: 13, color: '#374151', marginBottom: 4 }}>{label}</div>
    {children}
  </label>
);

const page: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f8fafc',
  padding: 20,
};

const card: React.CSSProperties = {
  width: '100%',
  maxWidth: 440,
  background: '#fff',
  padding: 32,
  borderRadius: 8,
  border: '1px solid #e5e7eb',
};

const input: React.CSSProperties = {
  width: '100%',
  padding: 8,
  border: '1px solid #d1d5db',
  borderRadius: 4,
  fontSize: 14,
  boxSizing: 'border-box',
};

// Sklonjeno iz vidokruga i iz tab reda, ali NE `display:none` — deo botova
// preskače polja koja su display:none/hidden.
const honeypot: React.CSSProperties = {
  position: 'absolute',
  left: '-9999px',
  width: 1,
  height: 1,
  overflow: 'hidden',
};

const primaryBtn: React.CSSProperties = {
  width: '100%',
  background: '#0f172a',
  color: '#fff',
  border: 'none',
  padding: '12px 16px',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 500,
};

export default Register;
